import React from "react";
import { Tag } from "antd";
import { CarOutlined, RightOutlined } from "@ant-design/icons";
import { frenchDateOnly } from "../../../variables/functions";
import { highlightMatch } from "../../../variables/stringFunctions";

// Métadonnées des avenants : libellé lisible + couleur du tag.
const AVENANT_META = {
  AN: { label: "Affaire nouvelle", color: "blue" },
  RN: { label: "Renouvellement", color: "geekblue" },
  AG: { label: "Augmentation", color: "green" },
  DG: { label: "Diminution", color: "orange" },
  IN: { label: "Incorporation", color: "cyan" },
  PR: { label: "Prorogation", color: "purple" },
  TG: { label: "Transfert", color: "magenta" },
  RV: { label: "Remise en vigueur", color: "lime" },
  RT: { label: "Retrait véhicule", color: "volcano" },
  SP: { label: "Suspension", color: "gold" },
  RS: { label: "Résiliation", color: "red" },
  AD: { label: "Duplicata", color: "default" },
  AO: { label: "Avenant d'ordre", color: "default" },
  OE: { label: "Avenant d'ordre", color: "default" },
};

const avenantMeta = (code) => AVENANT_META[code] || { label: code || "Avenant", color: "default" };

const souscripteurOf = (it) =>
  (it?.prenom ? `${it?.nom || ""} ${it?.prenom}` : `${it?.nom || ""}`).trim() || "—";

/**
 * Rendu pro de la palette de recherche contrats :
 * - regroupe les périodes d'une même police (affaire nouvelle + renouvellements) sous un seul bloc,
 * - badge chaque période par son avenant (AN, RN, …),
 * - date d'effet en date seule (plus d'heure « 00:00:00 » ni de tirets vides).
 */
export function renderContratSearchResults(results, searchValue, navigate) {
  if (!results || results.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "40px 24px", color: "#94a3b8", fontSize: 14 }}>
        Aucun contrat trouvé.
      </div>
    );
  }

  // Groupement par police (fallback police M puis immatriculation).
  const groups = new Map();
  results.forEach((it) => {
    const key = it?.numeroPoliceP || it?.numeroPoliceM || it?.immatriculation || it?.codeId;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(it);
  });

  const hl = (text) => highlightMatch(text?.toString() ?? "", searchValue);

  return (
    <div style={{ padding: "6px 8px" }}>
      {[...groups.entries()].map(([key, periods]) => {
        // Périodes triées de la plus récente à la plus ancienne.
        const sorted = [...periods].sort(
          (a, b) => new Date(b?.dateEffet || 0) - new Date(a?.dateEffet || 0)
        );
        const head = sorted[0];
        const police = head?.numeroPoliceP || head?.numeroPoliceM || "—";
        const multi = sorted.length > 1;

        return (
          <div
            key={key}
            style={{
              border: "1px solid #eef1f6",
              borderRadius: 12,
              marginBottom: 10,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            {/* En-tête du groupe : assuré + immatriculation + police */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "8px 12px",
                background: "#f8fafc",
                borderBottom: "1px solid #eef1f6",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span
                  style={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#0f172a",
                    background: "#e0f2fe",
                    border: "1px solid #bae6fd",
                    borderRadius: 6,
                    padding: "1px 8px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    whiteSpace: "nowrap",
                  }}
                >
                  <CarOutlined /> {hl(head?.immatriculation)}
                </span>
                <span
                  style={{
                    fontWeight: 600,
                    color: "#242166",
                    fontSize: 13,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {hl(souscripteurOf(head))}
                </span>
              </div>
              <span style={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>
                {hl(police)}
                {multi && (
                  <Tag color="geekblue" style={{ marginLeft: 6 }}>
                    {sorted.length} périodes
                  </Tag>
                )}
              </span>
            </div>

            {/* Une ligne cliquable par période */}
            {sorted.map((it) => {
              const meta = avenantMeta(it?.codeAvenant || it?.avenant);
              return (
                <div
                  key={it?.codeId}
                  onClick={() => navigate(`/details-contrat/${it?.codeId}`)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "9px 12px",
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <Tag color={meta.color} style={{ margin: 0, minWidth: 118, textAlign: "center" }}>
                      {meta.label}
                    </Tag>
                    <span style={{ color: "#334155", fontSize: 13, whiteSpace: "nowrap" }}>
                      Effet <b>{frenchDateOnly(it?.dateEffet) || "—"}</b>
                      {it?.dateEcheance ? (
                        <>
                          {" "}
                          <span style={{ color: "#cbd5e1" }}>→</span> {frenchDateOnly(it?.dateEcheance)}
                        </>
                      ) : null}
                    </span>
                    {it?.raisonSociale ? (
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: 12,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        · {it.raisonSociale}
                      </span>
                    ) : null}
                  </div>
                  <RightOutlined style={{ color: "#cbd5e1", fontSize: 12 }} />
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

export default renderContratSearchResults;
